import { ContractDataFieldNameDto } from "../../../../../autogen/bff-api";
import { useAppDispatch } from "../../../../../common/redux/hooks";
import { editContractThunk } from "../../../../../common/redux/thunks/contract/edit-contract-thunk";
import { requireDataFields } from "../../../typeguards";
import { useContractState } from "../../useContractState";
import { ContractOptions } from "./Options";
import { AwardDate } from "./dates/AwardDate";
import { ExpirationDate } from "./dates/ExpirationDate";
import { NoticePeriod } from "./dates/NoticePeriod";
import { RenewalDate } from "./dates/RenewalDate";
import { StartDate } from "./dates/StartDate";
import { TerminationDate } from "./dates/TerminationDate";
import { WarrantyExpirationDate } from "./dates/WarrantyExpirationDate";
import { EstimatedValue } from "./financial/EstimatedValue";
import { IfrsValues } from "./financial/IfrsValues";
import { PricePerMonth } from "./financial/PricePerMonth";
import { PricePerYear } from "./financial/PricePerYear";
import { TotalLiability } from "./financial/TotalLiability";
import { TotalPrice } from "./financial/TotalPrice";
import { CounterpartyContactPerson } from "./other/CounterpartyContactPerson";
import { DeliveryTerms } from "./other/DeliveryTerms";
import { EditProjects } from "./other/EditProjects";
import { ExternalLink } from "./other/ExternalLink";
import { InternalParties } from "./other/InternalParties";
import { InternalReferenceNumber } from "./other/InternalReferenceNumber";
import { LinkedContacts } from "./other/LinkedContracts";
import { OriginSourcingEvent } from "./other/OriginSourcingEvent";
import { PaymentTermsInDays } from "./other/PaymentTermsInDays";
import { Priority } from "./other/Priority";
import { PurchasingPolicy } from "./other/PurchasingPolicy";
import { Risk } from "./other/Risk";
import { SupplierCategory } from "./other/SupplierCategory";
import { SupplierInfo } from "./other/SupplierInfo";

interface Props {
  name: ContractDataFieldNameDto;
}

export const DataField = ({ name }: Props) => {
  const dispatch = useAppDispatch();
  const contractState = useContractState();
  const dataFields = requireDataFields(contractState.contract);
  const isRequired = dataFields.requiredDataFields.map((e) => e.name).includes(name);
  const removeDataField = async () => {
    await dispatch(
      editContractThunk({
        command: {
          type: "RemoveDataField",
          value: name,
        },
      })
    );
  };

  switch (name) {
    case ContractDataFieldNameDto.AwardDate:
      return <AwardDate removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />;
    case ContractDataFieldNameDto.RenewalDate:
      return (
        <RenewalDate removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.CounterpartyContactPerson:
      return (
        <CounterpartyContactPerson
          removeDataField={!isRequired ? removeDataField : undefined}
          isRequiredButNotProvided={false}
        />
      );
    case ContractDataFieldNameDto.DeliveryTerms:
      return (
        <DeliveryTerms removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.EstimatedValue:
      return (
        <EstimatedValue removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.EndDate:
      return (
        <ExpirationDate removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.ExternalLink:
      return (
        <ExternalLink removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.InternalReferenceNumber:
      return (
        <InternalReferenceNumber
          removeDataField={!isRequired ? removeDataField : undefined}
          isRequiredButNotProvided={false}
        />
      );
    case ContractDataFieldNameDto.LinkedContracts:
      return (
        <LinkedContacts removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.OriginSourcingEvent:
      return (
        <OriginSourcingEvent
          removeDataField={!isRequired ? removeDataField : undefined}
          isRequiredButNotProvided={false}
        />
      );
    case ContractDataFieldNameDto.PaymentTermsInDays:
      return (
        <PaymentTermsInDays
          removeDataField={!isRequired ? removeDataField : undefined}
          isRequiredButNotProvided={false}
        />
      );
    case ContractDataFieldNameDto.PricePerMonth:
      return (
        <PricePerMonth removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.PricePerYear:
      return (
        <PricePerYear removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.Priority:
      return <Priority removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />;
    case ContractDataFieldNameDto.PurchasingPolicy:
      return (
        <PurchasingPolicy
          removeDataField={!isRequired ? removeDataField : undefined}
          isRequiredButNotProvided={false}
        />
      );
    case ContractDataFieldNameDto.Risk:
      return <Risk removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />;
    case ContractDataFieldNameDto.StartDate:
      return <StartDate removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />;
    case ContractDataFieldNameDto.TotalLiability:
      return (
        <TotalLiability removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.TotalPrice:
      return (
        <TotalPrice removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.WarrantyExpirationDate:
      return (
        <WarrantyExpirationDate
          removeDataField={!isRequired ? removeDataField : undefined}
          isRequiredButNotProvided={false}
        />
      );
    case ContractDataFieldNameDto.TerminationDate:
      return <TerminationDate removeDataField={removeDataField} isRequiredButNotProvided={false} />;
    case ContractDataFieldNameDto.IfrsValues:
      return (
        <IfrsValues removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.InternalParties:
      return (
        <InternalParties removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.SupplierInfo:
      return (
        <SupplierInfo removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.SupplierCategory:
      return (
        <SupplierCategory
          removeDataField={!isRequired ? removeDataField : undefined}
          isRequiredButNotProvided={false}
        />
      );
    case ContractDataFieldNameDto.NoticePeriod:
      return (
        <NoticePeriod removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
    case ContractDataFieldNameDto.Options:
      return <ContractOptions />;
    case ContractDataFieldNameDto.Projects:
      return (
        <EditProjects removeDataField={!isRequired ? removeDataField : undefined} isRequiredButNotProvided={false} />
      );
  }
};
